import React, { ReactElement } from 'react';
import { Box, Typography, Button } from '@mui/material';
import {
  gray40,
  gray80,
  gray100,
  green50,
  red50,
  orange50,
  white,
} from '../../../../constants/colors';
import { regularSm, regularMd } from '../../../../constants/location';
import { COMPANY_DETAIL_FEATURE_CHANGE } from '../../../../constants/labels';

const styles = {
  row: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: `1px solid ${gray40}`,
    '&:last-of-type': {
      borderBottom: 'none',
    },
    '@media screen and (max-width:960px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      position: 'relative',
    },
  },
  label: {
    ...regularSm,
    color: gray80,
    // 最長ラベル「メール取り込み機能」に合わせて値の開始位置を揃える
    minWidth: '140px',
    textAlign: 'left',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    '@media screen and (max-width:960px)': {
      minWidth: 'auto',
      marginBottom: '4px',
    },
  },
  value: {
    ...regularMd,
    color: gray100,
    flex: 1,
    textAlign: 'left',
    marginLeft: '24px',
    wordBreak: 'break-word',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    '@media screen and (max-width:960px)': {
      marginLeft: 0,
      width: '100%',
    },
  },
  statusDot: {
    display: 'inline-block',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    marginRight: '8px',
  },
  changeButton: {
    height: '32px',
    padding: '8px 16px',
    fontSize: '12px',
    color: orange50,
    border: `1px solid ${orange50}`,
    backgroundColor: white,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: 'rgba(255, 107, 53, 0.08)',
      border: `1px solid ${orange50}`,
    },
    '@media screen and (max-width:960px)': {
      position: 'absolute',
      top: '36px',
      right: 0,
    },
  },
};

type FeatureToggleRowProps = {
  /** 行のラベル（例: マスタ管理機能） */
  label: string;
  /** 状態ドットの色を決める。true で緑・false で赤 */
  enabled: boolean;
  /** 状態テキスト（例: 有効 / 一時無効化中） */
  statusText: string;
  /** 変更ボタンを表示するか（運営管理者のみ true を渡す） */
  showChangeButton: boolean;
  /** 変更ボタン押下時の処理 */
  onChangeClick?: () => void;
};

/**
 * 企業詳細画面のオプション機能セクションで使う 1 行分のコンポーネント（#3193）。
 * 「ラベル / 状態ドット + 状態テキスト / 変更ボタン」の繰り返しを 1 箇所にまとめる。
 */
const FeatureToggleRow = ({
  label,
  enabled,
  statusText,
  showChangeButton,
  onChangeClick,
}: FeatureToggleRowProps): ReactElement => (
  <Box sx={styles.row}>
    <Typography sx={styles.label} component="div">
      {label}
    </Typography>
    <Box sx={styles.value}>
      <Typography component="span" sx={{ ...regularMd, color: gray100 }}>
        <Box
          component="span"
          sx={[styles.statusDot, { backgroundColor: enabled ? green50 : red50 }]}
        />
        {statusText}
      </Typography>
      {showChangeButton && (
        <Button sx={styles.changeButton} onClick={onChangeClick}>
          {COMPANY_DETAIL_FEATURE_CHANGE}
        </Button>
      )}
    </Box>
  </Box>
);

export default FeatureToggleRow;
